<template>
  <client-only>
    <div class="relative group w-full overflow-hidden">
      <button
        @click="scroll('left')"
        class="absolute cursor-pointer left-2 top-1/2 -translate-y-1/2 z-10 bg-white/80 p-2 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <svg
          class="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>

      <div
        ref="container"
        class="flex scrollbar-hide justify-between gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory"
        @scroll="handleInfiniteScroll"
      >
        <div
          v-for="(item, index) in displayItems"
          :key="index"
          :style="{ '--items-per-view': visibleItems }"
          class="snap-start flex-shrink-0 flex justify-center items-center"
        >
          <slot :item="item" />
        </div>
      </div>

      <button
        @click="scroll('right')"
        class="absolute cursor-pointer right-2 top-1/2 -translate-y-1/2 z-10 bg-white/80 p-2 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <svg
          class="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 5l7 7-7 7"
          />
        </svg>
      </button>
    </div>
  </client-only>
</template>

<script setup lang="ts">
// 1. Explicit Types
const props = withDefaults(
  defineProps<{
    items: any[];
    visibleItems?: number;
  }>(),
  {
    visibleItems: 3,
  },
);

const container = ref<HTMLElement | null>(null);

// 2. Safer Computed
const displayItems = computed(() => {
  if (!props.items?.length) return [];
  return [...props.items, ...props.items, ...props.items];
});

const scroll = (direction: "left" | "right") => {
  if (!container.value) return;
  const { clientWidth } = container.value;
  const itemWidth = clientWidth / props.visibleItems;
  const move = direction === "left" ? -itemWidth : itemWidth;
  container.value.scrollBy({ left: move, behavior: "smooth" });
};

const handleInfiniteScroll = () => {
  const el = container.value;
  if (!el) return;

  const { scrollLeft, scrollWidth } = el;
  const oneThird = scrollWidth / 3;

  // Use a 10px buffer to prevent "bouncing" loops
  if (scrollLeft <= 5) {
    el.scrollLeft = oneThird;
  } else if (scrollLeft >= oneThird * 2 - 5) {
    el.scrollLeft = oneThird;
  }
};

onMounted(() => {
  setTimeout(() => {
    if (container.value && props.items.length) {
      container.value.scrollLeft = container.value.scrollWidth / 3;
    }
  }, 100);
});
</script>

<style scoped>
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.carousel-item {
  /* The math stays in CSS, where it belongs */
  min-width: calc(
    (100% - (var(--items-per-view) - 1) * 16px) / var(--items-per-view)
  );
}
</style>
